/* H&H capsule release 5.1.0. Shopify enforces all prices and eligibility.
 * No Admin API credentials or zero-priced variants. Ordinary cart items can change their existing selling plan.
 * Capsule offer lines remain one-time. Existing subscription contracts are never edited.
 * Pure model/transport, also loadable in Node for tests. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.HHOfferCartCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const VERSION = 'hh-capsule-live-v5';
  const LEGACY = ['hh-capsule-qa-v4', 'hh-capsule-qa-v2'];
  const num = n => Number.isSafeInteger(n) && n >= 0;
  const upper = s => String(s || '').trim().toUpperCase();
  const props = l => l.properties || {};
  const isOwned = l => [VERSION, ...LEGACY].includes(props(l)._hh_commerce);
  const isSubscription = l => !!l.selling_plan_allocation;
  const isBundle = l => !!l.parent_relationship?.parent || !!l.item_components?.length || l.has_components === true;
  const qty = lines => lines.reduce((n, l) => n + l.quantity, 0);
  const sum = (lines, key) => lines.reduce((n, l) => n + (num(l[key]) ? l[key] : NaN), 0);
  const operationLines = (cart, id) => cart.items.filter(l => isOwned(l) && props(l)._hh_group === id);
  class OfferError extends Error {
    constructor(message, code = 'OFFER_ERROR') { super(message); this.name = 'OfferError'; this.code = code; }
  }
  // Match only the exact variant's allocations; a product-level plan is not
  // necessarily available for every size. Do not infer a 15% discount or plan ID.
  const planId = line => String(line.selling_plan_allocation?.selling_plan?.id || line.selling_plan_allocation?.selling_plan_id || '');
  function lineFamily(line) {
    return JSON.stringify([String(line.variant_id), Object.keys(props(line)).sort().map(k => [k, props(line)[k]])]);
  }
  function productHandle(line) {
    if (typeof line.handle === 'string' && line.handle) return line.handle;
    const match = String(line.url || '').match(/\/products\/([^/?#]+)/);
    if (match) { try { return decodeURIComponent(match[1]); } catch (_) { /* fail below */ } }
    throw new OfferError('Product purchase options are unavailable. Please use its product page.', 'PRODUCT');
  }
  function purchaseOptions(product, variantId) {
    const variant = product?.variants?.find(v => String(v.id) === String(variantId));
    if (!variant) throw new OfferError('This exact variant could not be verified. Refresh its product page.', 'PRODUCT');
    const allocations = new Map((variant.selling_plan_allocations || []).map(a => [String(a.selling_plan_id ?? a.selling_plan?.id), a]));
    const plans = [], seen = new Set();
    for (const group of product.selling_plan_groups || []) {
      for (const plan of group.selling_plans || []) {
        const id = String(plan.id), allocation = allocations.get(id);
        if (!/^\d+$/.test(id) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0 || !allocation || seen.has(id)) continue;
        seen.add(id);
        const groupName = String(group.name || '').trim(), name = String(plan.name || 'Subscription').trim();
        plans.push({ id, name, groupName, label: groupName && groupName !== name ? `${groupName} / ${name}` : name,
          recurring: plan.recurring_deliveries !== false, description: String(plan.description || ''), price: allocation.price });
      }
    }
    return { variantId: String(variant.id), oneTimeAllowed: product.requires_selling_plan !== true && variant.requires_selling_plan !== true,
      regularPrice: variant.price, plans };
  }
  function planCounts(cart, pending) {
    const family = cart.items.filter(l => lineFamily(l) === pending.family);
    return { from: qty(family.filter(l => planId(l) === pending.fromPlan)), to: qty(family.filter(l => planId(l) === pending.toPlan)) };
  }
  function planChangeConfirmed(cart, pending) {
    const counts = planCounts(cart, pending);
    return counts.from === pending.fromBefore - pending.n && counts.to === pending.toBefore + pending.n;
  }
  function codesIn(cart) {
    const codes = new Set((cart.discount_codes || []).map(d => upper(d.code)).filter(Boolean));
    const applications = [...(cart.cart_level_discount_applications || [])];
    for (const l of cart.items || []) for (const a of l.line_level_discount_allocations || []) applications.push(a.discount_application);
    for (const a of applications) if (a?.type === 'discount_code') codes.add(upper(a.code || a.title));
    return [...codes].filter(Boolean);
  }
  function groupModel(cart, config) {
    const items = cart.items || [], groups = [], included = new Set();
    for (const offer of config.catalog) {
      const same = items.filter(l => String(l.variant_id) === String(offer.variantId) && !isSubscription(l) && !isBundle(l));
      if (!same.some(isOwned)) continue;
      same.forEach(l => included.add(l));
      const quantity = qty(same), bonus = same.some(l => isOwned(l) && props(l)._hh_bonus === 'on');
      const giftEligible = bonus && quantity >= 3;
      groups.push({ id: offer.id, offer, lines: same, quantity, bonus, giftEligible,
        paidQuantity: quantity - Number(giftEligible), actual: sum(same, 'final_line_price'),
        original: sum(same, 'original_line_price'), expected: (quantity - Number(giftEligible)) * offer.unitPence,
        expectedSaving: giftEligible ? offer.unitPence : 0 });
    }
    return { groups, other: items.filter(l => !included.has(l)) };
  }
  function audit(cart, config) {
    const model = groupModel(cart, config), issues = [];
    for (const g of model.groups) {
      g.issues = [];
      const fail = text => { g.issues.push(text); issues.push(text); };
      const o = g.offer, name = o.title;
      if (cart.currency !== 'GBP') fail(`${name}: this offer is available in GBP. Choose GBP or remove the offer to use standard shopping.`);
      if (o.unitPence !== o.approvedUnitPence || !num(o.unitPence)) fail(`${name}: the current price no longer matches this offer. Please remove it and review the product page.`);
      if (!Number.isInteger(g.quantity) || g.quantity < 1 || g.quantity > 99) fail(`${name}: choose between 1 and 99 jars.`);
      for (const l of g.lines) {
        if (isOwned(l) && (props(l)._hh_offer !== o.id || !['on', 'off'].includes(props(l)._hh_bonus))) fail(`${name}: the saved offer selection is incomplete. Remove it and choose again.`);
        if (l.original_price !== o.unitPence || l.original_line_price !== l.quantity * o.unitPence) fail(`${name}: Shopify returned a different regular price. Please review the offer.`);
      }
      if (g.actual !== g.expected) fail(`${name}: the offer price could not be confirmed. Please recheck or remove this offer; no increased price has been accepted.`);
      const relevant = g.lines.flatMap(l => l.line_level_discount_allocations || []).filter(a => a.discount_application?.type === 'discount_code' && upper(a.discount_application.code || a.discount_application.title) === upper(o.code));
      const allocated = relevant.reduce((n, a) => n + (num(a.amount) ? a.amount : 0), 0);
      if (g.giftEligible && allocated !== o.unitPence) fail(`${name}: Shopify has not confirmed exactly one free jar with the offer discount.`);
      if (!g.giftEligible && allocated !== 0) fail(`${name}: a free-jar discount is still applied to a selection without the free jar. Recheck the basket.`);
      g.valid = g.issues.length === 0;
    }
    // A manipulated marker must not make an unknown/subscription item disappear.
    if (model.other.some(isOwned)) issues.push('An unrecognised or subscription item is marked as an offer. Remove that item before continuing.');
    if (model.groups.length) {
      const names = new Set(config.catalog.map(o => upper(o.code)));
      if (model.other.some(l => (l.line_level_discount_allocations || []).some(a => a.discount_application?.type === 'discount_code' && names.has(upper(a.discount_application.code || a.discount_application.title))))) issues.push('An offer discount was also allocated outside its eligible one-time capsule selection. Please review the basket.');
      if ((cart.cart_level_discount_applications || []).some(a => a.target_type !== 'shipping_line' && Number(a.total_allocated_amount) > 0)) issues.push('Another order discount conflicts with the capsule offer. Remove the extra code or the capsule offer to continue.');
    }
    if (!num(cart.total_price) || !num(cart.items_subtotal_price) || sum(cart.items || [], 'final_line_price') !== cart.items_subtotal_price) issues.push('The basket totals could not be confirmed. Please refresh before continuing.');
    return { ...model, valid: issues.length === 0, issues: [...new Set(issues)], total: cart.total_price,
      savings: model.groups.filter(g => g.valid).reduce((n, g) => n + g.expectedSaving, 0) };
  }
  function desiredCodes(cart, config, removeExtra = false) {
    const own = new Set([...(config.knownCodes || []), ...config.catalog.map(o => o.code)].map(upper));
    const keep = removeExtra ? [] : codesIn(cart).filter(c => !own.has(c));
    for (const g of groupModel(cart, config).groups) if (g.giftEligible) keep.push(upper(g.offer.code));
    return [...new Set(keep)].filter(Boolean);
  }
  function assertCatalog(o) {
    if (!o || !/^\d+$/.test(String(o.variantId))) throw new OfferError('This product is not configured for the capsule offer.', 'CONFIG');
    if (!num(o.unitPence) || o.unitPence !== o.approvedUnitPence) throw new OfferError('This offer price has changed. Please use the standard product page.', 'PRICE');
    if (!o.available) throw new OfferError('This 60-capsule jar is currently unavailable.', 'STOCK');
    if (!o.code || /CONFIGURE|REPLACE|YOUR[-_ ]?CODE/i.test(o.code) || /[,\s]/.test(o.code)) throw new OfferError('The capsule promotion is not available yet. Please use the standard product page.', 'CONFIG');
  }
  class Client {
    constructor(config, deps = {}) {
      this.config = config; this.fetch = deps.fetch || globalThis.fetch.bind(globalThis);
      this.storage = deps.storage || null; this.lock = deps.lock || (fn => fn());
      this.uuid = deps.uuid || (() => {
        if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
        if (typeof globalThis.crypto?.getRandomValues !== 'function') throw new OfferError('This browser cannot safely identify the basket action. Please use a current browser.', 'BROWSER');
        return 'hh-' + [...globalThis.crypto.getRandomValues(new Uint8Array(16))].map(n => n.toString(16).padStart(2, '0')).join('');
      });
      this.timeout = deps.timeout || 20000; this.busy = false; this.lastCart = null;
      this.pendingKey = 'hh-capsule-pending-v5';
      this.productCache = new Map();
    }
    pending() { try { return JSON.parse(this.storage?.getItem(this.pendingKey) || 'null'); } catch (_) { return null; } }
    setPending(p) {
      if (!this.storage) throw new OfferError('Your browser could not save this basket action. Allow site storage or use the standard product page.', 'STORAGE');
      try { if (p) { this.storage.setItem(this.pendingKey, JSON.stringify(p)); if (!this.pending()) throw Error(); } else this.storage.removeItem(this.pendingKey); }
      catch (_) { throw new OfferError('This basket action could not be recorded safely. No automatic retry was made.', 'STORAGE'); }
    }
    clearCheck() { if (this.pending()) throw new OfferError('A previous basket action is not confirmed. Use Recheck basket before adding again.', 'PENDING'); }
    async exclusive(fn) {
      if (this.busy) throw new OfferError('A basket change is already in progress.', 'BUSY');
      this.busy = true; try { return await this.lock(fn); } finally { this.busy = false; }
    }
    async request(path, body) {
      const abort = new AbortController(), timer = setTimeout(() => abort.abort(), this.timeout);
      try {
        const response = await this.fetch(this.config.root + path, { method: body ? 'POST' : 'GET',
          credentials: 'same-origin', cache: 'no-store', signal: abort.signal,
          headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
          ...(body ? { body: JSON.stringify(body) } : {}) });
        const data = await response.json();
        if (!response.ok) throw new OfferError(String(data.description || data.message || 'Shopify could not update the basket.'), `HTTP_${response.status}`);
        return data;
      } catch (e) { if (e instanceof OfferError) throw e; throw new OfferError('The basket response was interrupted. Recheck it before trying again.', 'UNCERTAIN'); }
      finally { clearTimeout(timer); }
    }
    async read() {
      const c = await this.request('cart.js');
      if (!Array.isArray(c.items)) throw new OfferError('The basket could not be loaded.', 'CART');
      this.lastCart = c; return c;
    }
    async syncCodes(cart, removeExtra = false) {
      const desired = desiredCodes(cart, this.config, removeExtra), existing = codesIn(cart);
      const applicable = desired.every(c => !(cart.discount_codes || []).some(d => upper(d.code) === c && d.applicable === false));
      if (applicable && desired.slice().sort().join(',') === existing.slice().sort().join(',')) return cart;
      await this.request('cart/update.js', { discount: desired.join(',') });
      return this.read();
    }
    requireActive() { if (!this.config.active) throw new OfferError('New capsule-offer purchases are paused.', 'GATE'); }
    async removeOperation(id) {
      for (let i = 0; i < 30; i++) {
        const c = await this.read(), l = operationLines(c, id)[0]; if (!l) return c;
        await this.request('cart/change.js', { id: l.key, quantity: 0 });
      }
      throw new OfferError('The basket kept changing. Please refresh and review it.', 'CONFLICT');
    }
    async rollback(id) {
      await this.removeOperation(id); const c = await this.syncCodes(await this.read());
      if (operationLines(c, id).length) throw new OfferError('Removal has not been confirmed. Recheck the basket.', 'PENDING');
      this.setPending(null); return c;
    }
    async add(selection) {
      return this.exclusive(async () => {
        this.requireActive(); this.clearCheck();
        const o = this.config.catalog.find(x => String(x.variantId) === String(selection.variant_id)); assertCatalog(o);
        const n = selection.quantity, gift = n === 3 && selection.free_month_enabled === true;
        if (selection.quantity_kind !== 'jars' || ![1, 2, 3].includes(n) || (n === 3) !== gift || selection.currency !== 'GBP' || selection.expected_payable_pence !== o.unitPence * (n === 1 ? 1 : 2)) throw new OfferError('The selected offer and product price do not agree. Please reload the page.', 'SELECTION');
        let before = await this.read();
        // Preserve a pre-existing ordinary promotion. Do not silently remove its
        // code, absorb its discount, or claim a second free jar.
        const plain = before.items.filter(l => String(l.variant_id) === String(o.variantId) && !isSubscription(l));
        if (!plain.some(isOwned) && plain.some(l => l.final_line_price < l.original_line_price)) {
          const codeAmount = plain.flatMap(l => l.line_level_discount_allocations || []).filter(a =>
            a.discount_application?.type === 'discount_code' && upper(a.discount_application.code || a.discount_application.title) === upper(o.code)
          ).reduce((n, a) => n + (a.amount || 0), 0);
          if (gift && qty(plain) >= 3 && codeAmount === o.unitPence && !plain.some(isBundle) &&
              plain.every(l => l.original_price === o.unitPence) && sum(plain, 'final_line_price') === (qty(plain) - 1) * o.unitPence) {
            // Add display metadata to one existing line, not merchandise. All
            // plain units of that variant then share the visible offer group.
            const line = plain[0], id = this.uuid();
            this.setPending({ kind: 'change', offerId: o.id, n: qty(plain), bonus: true, timestamp: Date.now() });
            try {
              await this.request('cart/change.js', { id: line.key, quantity: line.quantity,
                properties: { ...props(line), _hh_commerce: VERSION, _hh_group: id, _hh_offer: o.id, _hh_bonus: 'on' } });
              const adopted = await this.read(); const result = audit(adopted, this.config);
              if (!result.valid) throw new OfferError(result.issues.join(' '), 'AUDIT');
              this.setPending(null);
              return { cart: adopted, alreadyPresent: true, message: `${o.title} already has its free-jar offer in your basket. Its existing units are shown below; no additional jars were added.` };
            } catch (e) {
              if (e.code?.startsWith('HTTP_')) this.setPending(null);
              throw e;
            }
          }
          throw new OfferError('This product already has a different promotion in the basket. Review or remove those items before choosing this offer.', 'EXISTING_PROMOTION');
        }
        before = await this.syncCodes(before);
        const pre = audit(before, this.config);
        if (before.currency !== 'GBP') throw new OfferError('This capsule campaign is available in GBP. Use the standard product page for other currencies.', 'CURRENCY');
        if (!pre.valid) throw new OfferError(pre.issues.join(' '), 'AUDIT');
        const existing = pre.groups.find(g => g.id === o.id);
        if (gift && existing?.giftEligible) return { cart: before, alreadyPresent: true, message: `${o.title}'s free-jar offer is already in your basket. Extra jars can be added there at the regular price.` };
        const same = before.items.filter(l => String(l.variant_id) === String(o.variantId) && !isSubscription(l));
        if (same.some(isBundle)) throw new OfferError('This variant is already part of another bundle. Please review that bundle before adding this offer.', 'BUNDLE');
        if (same.some(l => l.original_price !== o.unitPence)) throw new OfferError('An existing item has different pricing. Please review the basket before adding this offer.', 'PRICE');
        if (qty(same) + n > 99) throw new OfferError('Please use the basket to adjust this quantity.', 'QUANTITY');
        const id = this.uuid(), bonus = gift || !!existing?.giftEligible;
        this.setPending({ kind: 'add', id, offerId: o.id, n, bonus, timestamp: Date.now() });
        let failure = null;
        try { await this.request('cart/add.js', { items: [{ id: Number(o.variantId), quantity: n,
          properties: { _hh_commerce: VERSION, _hh_group: id, _hh_offer: o.id, _hh_bonus: bonus ? 'on' : 'off' } }] }); }
        catch (e) { failure = e; }
        let c;
        try { c = await this.read(); } catch (_) { throw new OfferError('The add result is not confirmed. Do not repeat it; open the basket and recheck.', 'PENDING'); }
        const added = operationLines(c, id);
        if (!added.length && failure?.code === 'UNCERTAIN') throw new OfferError('The add may still be processing. Recheck the basket before trying again.', 'PENDING');
        if (qty(added) !== n || added.some(isSubscription)) {
          try { await this.rollback(id); } catch (_) { throw new OfferError('Only part of the selection may have been added. Recheck the basket to remove the unconfirmed addition.', 'PENDING'); }
          throw new OfferError(`${failure?.message || 'The selected quantity is not available.'} The unconfirmed addition was removed.`, 'ADD_REJECTED');
        }
        try {
          c = await this.syncCodes(c); const result = audit(c, this.config);
          if (!result.valid) throw new OfferError(result.issues.join(' '), 'AUDIT');
          if (qty(operationLines(c, id)) !== n) throw new OfferError('Another app changed the added quantity. Please review the basket.', 'CONFLICT');
          this.setPending(null); return { cart: c, added: n, offerId: o.id, operationId: id, message: `${o.title} added to your basket.` };
        } catch (e) {
          try { await this.rollback(id); } catch (_) { throw new OfferError(`${e.message} Recheck the basket to remove the unconfirmed addition.`, 'PENDING'); }
          throw new OfferError(`${e.message} The new addition was removed; your earlier items remain.`, 'ADD_REJECTED');
        }
      });
    }
    async changeGroupRaw(id, wanted, bonus) {
      let c;
      // Update all owned lines while retaining unrelated properties and fresh keys.
      for (let i = 0; i < 60; i++) {
        c = await this.read(); const g = groupModel(c, this.config).groups.find(x => x.id === id);
        if (!g) { if (wanted === 0) return c; throw new OfferError('This offer was changed in another window. Please refresh.', 'STALE'); }
        const mismatch = g.lines.find(l => isOwned(l) && props(l)._hh_bonus !== (bonus ? 'on' : 'off'));
        if (mismatch && wanted > 0) {
          await this.request('cart/change.js', { id: mismatch.key, quantity: mismatch.quantity, properties: { ...props(mismatch), _hh_bonus: bonus ? 'on' : 'off' } }); continue;
        }
        if (g.quantity === wanted) return c;
        // Remove pre-existing ordinary rows first so the offer marker survives reductions.
        const line = g.quantity > wanted ? (g.lines.find(l => !isOwned(l)) || g.lines[0]) : (g.lines.find(isOwned) || g.lines[0]);
        const target = g.quantity > wanted ? Math.max(0, line.quantity - (g.quantity - wanted)) : line.quantity + wanted - g.quantity;
        await this.request('cart/change.js', { id: line.key, quantity: target });
      }
      throw new OfferError('Another app kept changing these items. Please refresh the basket.', 'CONFLICT');
    }
    async changeGroup(id, wanted, bonus) {
      return this.exclusive(async () => {
        this.clearCheck();
        if (!Number.isInteger(wanted) || wanted < 0 || wanted > 99) throw new OfferError('Choose a quantity from 0 to 99.', 'QUANTITY');
        let c = await this.read(), g = groupModel(c, this.config).groups.find(x => x.id === id);
        if (!g) throw new OfferError('This offer is no longer in your basket.', 'STALE');
        if (wanted > g.quantity) { this.requireActive(); assertCatalog(g.offer); }
        bonus = !!bonus && wanted >= 3;
        this.setPending({ kind: 'change', offerId: id, n: wanted, bonus, timestamp: Date.now() });
        try {
          c = await this.changeGroupRaw(id, wanted, bonus); c = await this.syncCodes(c);
          const a = audit(c, this.config), now = a.groups.find(x => x.id === id);
          if ((now?.quantity || 0) !== wanted || (wanted > 0 && now.bonus !== bonus)) throw new OfferError('The final quantity could not be confirmed. Recheck your basket.', 'CONFLICT');
          this.setPending(null);
          if (!a.valid) throw new OfferError(a.issues.join(' '), 'AUDIT');
          return c;
        } catch (e) {
          if (e.code?.startsWith('HTTP_')) this.setPending(null); // Definitive rejection; display the actual current cart.
          throw e;
        }
      });
    }
    async changeOther(key, quantity) {
      return this.exclusive(async () => {
        this.clearCheck();
        if (!Number.isInteger(quantity) || quantity < 0 || quantity > 99) throw new OfferError('Choose a quantity from 0 to 99.', 'QUANTITY');
        let c = await this.read(); const line = c.items.find(l => l.key === key);
        if (!line) throw new OfferError('This item changed. Refresh the basket before editing it.', 'STALE');
        if (groupModel(c, this.config).groups.some(g => g.lines.some(l => l.key === key))) throw new OfferError('Use this product’s offer controls.', 'STALE');
        if (isBundle(line)) throw new OfferError('Manage this bundle on the standard cart page.', 'BUNDLE');
        await this.request('cart/change.js', { id: key, quantity });
        return this.syncCodes(await this.read());
      });
    }
    async productForLine(line, fresh = false) {
      const handle = productHandle(line), key = encodeURIComponent(handle);
      const previous = this.productCache.get(key);
      if (!fresh && previous && Date.now() - previous.at < 300000) return previous.promise;
      const promise = this.request(`products/${key}.js`).then(product => {
        if (line.product_id && String(product.id) !== String(line.product_id)) throw new OfferError('The product information does not match this item.', 'PRODUCT');
        purchaseOptions(product, line.variant_id);
        return product;
      });
      const entry = { at: Date.now(), promise }; this.productCache.set(key, entry);
      promise.catch(() => { if (this.productCache.get(key) === entry) this.productCache.delete(key); });
      return promise;
    }
    async changeSellingPlan(key, requested) {
      return this.exclusive(async () => {
        this.clearCheck();
        const toPlan = requested === null || requested === '' ? '' : String(requested);
        if (toPlan && (!/^\d+$/.test(toPlan) || !Number.isSafeInteger(Number(toPlan)) || Number(toPlan) <= 0)) throw new OfferError('Choose an available purchase option.', 'PLAN');
        let before = await this.read(), line = before.items.find(l => l.key === key);
        const checkLine = cartLine => {
          if (!cartLine) throw new OfferError('This item changed. Recheck the basket before choosing its purchase option.', 'STALE');
          if (isOwned(cartLine) || groupModel(before, this.config).groups.some(g => g.lines.some(l => l.key === cartLine.key))) throw new OfferError('The capsule offer is a one-time purchase. Use standard product options for a separate subscription.', 'OFFER_PLAN');
          if (isBundle(cartLine) || cartLine.gift_card) throw new OfferError('Manage this product through its standard product page.', 'BUNDLE');
        };
        checkLine(line);
        const originalFamily = lineFamily(line), originalPlan = planId(line), originalQuantity = line.quantity;
        if (originalPlan === toPlan) return before;
        const product = await this.productForLine(line, true), options = purchaseOptions(product, line.variant_id);
        if (toPlan ? !options.plans.some(p => p.id === toPlan) : !options.oneTimeAllowed) throw new OfferError('That purchase option is not available for this exact variant. Refresh the product options.', 'PLAN');
        // Product data is asynchronous. Re-read and resolve the current 1-based
        // line immediately before changing a selling plan (Shopify requires line,
        // not variant ID or key, for this operation). Never default quantity to 1.
        before = await this.read(); const index = before.items.findIndex(l => l.key === key); line = before.items[index]; checkLine(line);
        if (lineFamily(line) !== originalFamily || planId(line) !== originalPlan || line.quantity !== originalQuantity) throw new OfferError('This item changed while its options loaded. Please recheck the basket.', 'STALE');
        const pending = { kind: 'selling-plan', family: originalFamily, fromPlan: originalPlan, toPlan, n: line.quantity, timestamp: Date.now() };
        const counts = planCounts(before, pending); pending.fromBefore = counts.from; pending.toBefore = counts.to;
        this.setPending(pending);
        let failure = null;
        try { await this.request('cart/change.js', { line: index + 1, quantity: line.quantity, selling_plan: toPlan ? Number(toPlan) : null }); }
        catch (e) { failure = e; }
        let cart;
        try { cart = await this.read(); } catch (_) { throw new OfferError('The purchase-option change is not confirmed. Recheck the basket before trying again.', 'PENDING'); }
        if (!planChangeConfirmed(cart, pending)) {
          const now = planCounts(cart, pending), unchanged = now.from === pending.fromBefore && now.to === pending.toBefore;
          if (unchanged && (!failure || failure.code?.startsWith('HTTP_'))) {
            this.setPending(null);
            throw failure || new OfferError('Shopify did not apply that purchase option. The current basket is shown.', 'PLAN_REJECTED');
          }
          throw new OfferError('The purchase option could not be confirmed. Recheck it or explicitly keep the displayed option before continuing.', 'PENDING');
        }
        // Existing line keys can change or merge after a plan change. Verify
        // quantities by exact variant + properties + plan, then use fresh keys.
        cart = await this.syncCodes(cart);
        if (!planChangeConfirmed(cart, pending)) throw new OfferError('Another change affected the purchase option. Recheck the basket.', 'PENDING');
        this.setPending(null);
        const result = audit(cart, this.config);
        if (!result.valid) throw new OfferError(result.issues.join(' '), 'AUDIT');
        return cart;
      });
    }
    async recover() {
      return this.exclusive(async () => {
        let c = await this.read(); const p = this.pending();
        if (p) {
          const g = groupModel(c, this.config).groups.find(x => x.id === p.offerId);
          const matches = p.kind === 'selling-plan' ? planChangeConfirmed(c, p) : p.kind === 'add' ? qty(operationLines(c, p.id)) === p.n : (g?.quantity || 0) === p.n && (!p.n || g.bonus === p.bonus);
          if (!matches) return { cart: c, pending: true };
        }
        c = await this.syncCodes(c); if (audit(c, this.config).valid) this.setPending(null);
        return { cart: c, pending: !!this.pending() };
      });
    }
    async removePendingAddition() {
      return this.exclusive(async () => {
        const p = this.pending(); if (!p) return this.read();
        if (p.kind !== 'add') throw new OfferError('Recheck this edit or explicitly accept the quantities currently displayed.', 'PENDING');
        return this.rollback(p.id);
      });
    }
    async removePendingSelection() {
      return this.exclusive(async () => {
        const p = this.pending();
        if (!p) return this.read();
        if (p.kind === 'add') return this.rollback(p.id);
        if (p.kind === 'selling-plan') throw new OfferError('Recheck the purchase option or keep the displayed option. No product was automatically removed.', 'PENDING');
        // Explicit customer recovery: remove the displayed product selection,
        // never unrelated products. This is not an automatic retry of an add.
        const g = groupModel(await this.read(), this.config).groups.find(x => x.id === p.offerId);
        if (g) await this.changeGroupRaw(p.offerId, 0, false);
        const c = await this.syncCodes(await this.read()); this.setPending(null); return c;
      });
    }
    async acceptObservedEdit() {
      return this.exclusive(async () => {
        const p = this.pending(); if (p?.kind === 'add') throw new OfferError('Recheck or remove the unconfirmed addition first.', 'PENDING');
        let c = await this.syncCodes(await this.read());
        if (!audit(c, this.config).valid) throw new OfferError('The displayed basket is not valid yet. Please recheck or remove the offer.', 'AUDIT');
        this.setPending(null); return c;
      });
    }
    async removeExtraCodes() {
      return this.exclusive(async () => { this.clearCheck(); return this.syncCodes(await this.read(), true); });
    }
    async note(text) { return this.exclusive(async () => { await this.request('cart/update.js', { note: String(text).slice(0, 5000) }); return this.read(); }); }
    async checkout() {
      return this.exclusive(async () => {
        this.clearCheck(); const c = await this.syncCodes(await this.read()), a = audit(c, this.config);
        if (!c.items.length) throw new OfferError('Your basket is empty.', 'EMPTY');
        if (!a.valid) throw new OfferError(a.issues.join(' '), 'AUDIT');
        return c;
      });
    }
    diagnostic(c) {
      const a = audit(c, this.config);
      return { version: VERSION, currency: c.currency, valid: a.valid, issues: a.issues, subtotal: c.total_price,
        groups: a.groups.map(g => ({ offer: g.id, quantity: g.quantity, freeRequested: g.bonus, actual: g.actual, expected: g.expected,
          lines: g.lines.map(l => ({ quantity: l.quantity, original: l.original_line_price, final: l.final_line_price, subscription: isSubscription(l),
            allocations: (l.line_level_discount_allocations || []).map(x => ({ amount: x.amount, type: x.discount_application?.type, title: x.discount_application?.title })) })) })),
        other: a.other.map(l => ({ variantId: l.variant_id, quantity: l.quantity, final: l.final_line_price, subscription: isSubscription(l) })) };
    }
  }
  return { VERSION, OfferError, Client, audit, groupModel, codesIn, desiredCodes, assertCatalog, isOwned, isSubscription, isBundle, operationLines, planId, productHandle, purchaseOptions, planChangeConfirmed, lineFamily };
});
